import React, { useState, useRef, useEffect } from 'react';
import Draggable from 'react-draggable';

const AssistantPanel = ({ onClose, userPlan, onAction }) => {
  const chatRef = useRef(null);

  const initialActions = [
    { label: 'Agregar dispositivos', command: 'help_add_device' },
    { label: 'Editar dispositivos', command: 'help_edit_device' },
    { label: 'Descargar código', command: 'help_download_code' },
    { label: 'Controlar y monitorear dispositivos', command: 'help_control_device' }
  ];

  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: '¿En qué puedo ayudarte?',
      actions: initialActions
    }
  ]);

  const [input, setInput] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const recognitionRef = useRef(null);
  const [audioCache, setAudioCache] = useState({});

  // ✅ Scroll automático al nuevo mensaje
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  // ✅ Renderizar texto con Markdown básico
  const renderMarkdown = (text) => {
    return text.split('\n').map((line, i) => {
      let formattedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      let style = {};

      if (/^[-•]/.test(line.trim())) {
        style = { paddingLeft: '15px' };
        formattedLine = '• ' + formattedLine.replace(/^[-•]\s*/, '');
      }

      return (
        <div
          key={i}
          style={{ ...style, marginBottom: '4px' }}
          dangerouslySetInnerHTML={{ __html: formattedLine }}
        />
      );
    });
  };

  // ✅ Enviar mensaje al backend
  const handleSend = async () => {
    if (!input.trim()) return;
    const newMessages = [...messages, { role: 'user', text: input }];
    setMessages(newMessages);
    setInput('');
    await sendMessageToBackend(input);
  };

  const handleSendCustom = async (question) => {
    setMessages(prev => [...prev, { role: 'user', text: question }]);
    await sendMessageToBackend(question);
  };

  const sendMessageToBackend = async (message) => {
    try {
      const res = await fetch('https://holmio.net/app/assistant.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, userPlan })
      });
      const data = await res.json();

      if (data.message) {
        setMessages(prev => [
          ...prev,
          {
            role: 'assistant',
            text: data.message,
            actions: data.actions || []
          }
        ]);
      }
    } catch (error) {
      console.error('Error al conectar con el backend:', error);
    }
  };

  const showInitialOptions = () => {
    setMessages(prev => [
      ...prev,
      {
        role: 'assistant',
        text: '¿En qué puedo ayudarte?',
        actions: initialActions
      }
    ]);
  };

  const startVoiceRecognition = () => {
    if (!('webkitSpeechRecognition' in window)) {
      alert('Tu navegador no soporta reconocimiento de voz.');
      return;
    }
    const recognition = new window.webkitSpeechRecognition();
    recognition.lang = 'es-ES';
    recognition.interimResults = false;
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInput(transcript);
    };
    recognition.start();
    recognitionRef.current = recognition;
  };

  const speakText = async (text) => {
    try {
      if (audioCache[text]) {
        new Audio(audioCache[text]).play();
        return;
      }

      const formData = new FormData();
      formData.append('text', text);
      formData.append('voice', 'alloy');

      const response = await fetch('https://holmio.net/app/tts.php', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Error al generar audio');

      const blob = await response.blob();
      const audioURL = URL.createObjectURL(blob);

      setAudioCache(prev => ({ ...prev, [text]: audioURL }));
      new Audio(audioURL).play();
    } catch (error) {
      console.error('Error al reproducir la voz:', error);
    }
  };

  if (isMinimized) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: '90px',
          right: '20px',
          background: '#2563eb',
          color: '#fff',
          padding: '8px 12px',
          borderRadius: '8px',
          cursor: 'pointer',
          fontWeight: 'bold',
          zIndex: 10000
        }}
        onClick={() => setIsMinimized(false)}
      >
        Asistente (clic para abrir)
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '90px',
        right: '20px',
        width: '350px',
        height: '500px',
        background: '#fff',
        borderRadius: '10px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 10000,
        transform: 'translateX(0)',
        transition: 'all 0.3s ease-in-out'
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '10px',
          background: '#2563eb',
          color: '#fff',
          borderTopLeftRadius: '10px',
          borderTopRightRadius: '10px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <span>Asistente IA</span>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setIsMinimized(true)}
            style={{
              fontWeight: 'bold',
              background: 'transparent',
              color: '#fff',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            ⬇️
          </button>
          <button
            onClick={onClose}
            style={{
              fontWeight: 'bold',
              background: 'transparent',
              color: '#fff',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            ✖
          </button>
        </div>
      </div>

      {/* Chat */}
      <div ref={chatRef} style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
        {messages.map((msg, index) => (
          <div
            key={index}
            style={{
              marginBottom: '10px',
              textAlign: msg.role === 'user' ? 'right' : 'left'
            }}
          >
            <div
              style={{
                display: 'inline-block',
                background: msg.role === 'user' ? '#e5e7eb' : '#dbeafe',
                padding: '8px 12px',
                borderRadius: '8px',
                maxWidth: '95%'
              }}
            >
              <div>{renderMarkdown(msg.text)}</div>

              {msg.role === 'assistant' && (
                <>
                  <button
                    style={{
                      marginTop: '6px',
                      fontSize: '14px',
                      color: '#2563eb',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                    onClick={() => speakText(msg.text)}
                  >
                    🔊
                  </button>

                  {msg.actions && msg.actions.length > 0 && (
                    <div
                      style={{
                        marginTop: '8px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px'
                      }}
                    >
                      {msg.actions.map((action, i) => (
                        <button
                          key={i}
                          style={{
                            background: '#2563eb',
                            color: '#fff',
                            padding: '6px 10px',
                            borderRadius: '6px',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                          onClick={() => {
                            if (action.command.startsWith('help_')) {
                              let question = '';
                              if (action.command === 'help_add_device')
                                question = '¿Cómo agregar dispositivos?';
                              if (action.command === 'help_edit_device')
                                question = '¿Cómo editar dispositivos?';
                              if (action.command === 'help_download_code')
                                question = '¿Cómo descargar el código del dispositivo?';
                              if (action.command === 'help_control_device')
                                question = '¿Cómo controlar y monitorear dispositivos?';

                              handleSendCustom(question);
                            } else {
                              onAction(action.command);
                              setIsMinimized(true); // ✅ Minimiza en lugar de cerrar
                            }
                          }}
                        >
                          {action.label}
                        </button>
                      ))}

                      {!(msg.text.includes('¿En qué puedo ayudarte?')) && (
                        <button
                          style={{
                            background: '#6b7280',
                            color: '#fff',
                            padding: '6px 10px',
                            borderRadius: '6px',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                          onClick={showInitialOptions}
                        >
                          Ver opciones
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Input */}
      <div style={{ padding: '10px', display: 'flex', gap: '8px' }}>
        <button onClick={startVoiceRecognition} className="bg-gray-200 px-3 py-2 rounded">
          🎤
        </button>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Escribe tu pregunta..."
          style={{
            flex: 1,
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '8px'
          }}
        />
        <button
          onClick={handleSend}
          style={{
            backgroundColor: '#2563eb',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            padding: '8px 12px',
            cursor: 'pointer'
          }}
        >
          Enviar
        </button>
      </div>
    </div>
  );
};

export default AssistantPanel;
